import { useMemo } from 'react';
import type { SelectOptionObject } from '@looker/components';
import { keyBy } from 'lodash';

import { FieldSelect } from '@/components/looker/field-select';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useMe } from '@/hooks/use-me';

import { ListTops } from '../api/queries/list-tops';
import type { Top } from '../entities/top';

export type TopFilterProps = {
  value?: Top;
  onChange: (value?: Top) => void;
};

export function TopFilter({ value, onChange }: TopFilterProps) {
  const me = useMe();
  const currentAsset = useCurrentAsset();
  const listTops = ListTops.useQuery({ currentAsset }, { retry: 3 });
  const { asset, isMobility } = currentAsset;

  const filteredTops = useMemo(() => {
    if (isMobility) {
      return listTops.data?.tops ?? [];
    }

    if (!asset) {
      return [];
    }

    return (
      listTops.data?.tops.filter(
        ({ label }) =>
          label.includes(asset.fullFrontendId) ||
          label.includes(asset.fullFrontendId.replaceAll(' ', '_')),
      ) ?? []
    );
  }, [listTops.data?.tops, asset, isMobility]);

  const topOptions = useMemo<SelectOptionObject[]>(
    () =>
      filteredTops
        .map((top) => ({ label: top.label, value: top.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [filteredTops],
  );

  const topMap = useMemo<Record<string, Top>>(
    () => keyBy(filteredTops, 'id'),
    [filteredTops],
  );

  const label = useMemo(() => {
    return me.config.sitesWithDifferentLevels
      ? 'Top Level ID'
      : me.config.top.labels.id;
  }, [me.config]);

  return (
    <FieldSelect
      data-testid="top-filter"
      label={label}
      placeholder="Select&hellip;"
      className="select1"
      options={topOptions}
      isFilterable={true}
      isLoading={listTops.isLoading}
      value={value?.id}
      onChange={(id: string) => onChange(topMap[id])}
    />
  );
}
